import { useMemo } from 'react'
import { Pie } from 'react-chartjs-2'
import type { ChartData, ChartOptions } from 'chart.js'
import { ensureChartsRegistered } from './register'
import { CATEGORY_PALETTE } from './palette'

ensureChartsRegistered()

export interface PieChartProps {
  labels: string[]
  values: number[]
  colors?: readonly string[]
  showLegend?: boolean
  legendPosition?: 'top' | 'bottom' | 'left' | 'right'
  formatTooltip?: (label: string, value: number) => string
  onSliceClick?: (label: string, value: number) => void
  height?: number | string
  className?: string
  ariaLabel?: string
}

export function PieChart({
  labels,
  values,
  colors = CATEGORY_PALETTE,
  showLegend = true,
  legendPosition = 'bottom',
  formatTooltip,
  onSliceClick,
  height = 280,
  className,
  ariaLabel,
}: PieChartProps) {
  const data = useMemo<ChartData<'pie'>>(
    () => ({
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: labels.map((_, i) => colors[i % colors.length]),
          borderWidth: 1.5,
        },
      ],
    }),
    [labels, values, colors],
  )

  const options = useMemo<ChartOptions<'pie'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: onSliceClick
        ? (_evt, elements, chart) => {
            if (!elements.length) return
            const el = elements[0]
            const label = String(chart.data.labels?.[el.index] ?? '')
            const raw = chart.data.datasets[el.datasetIndex]?.data?.[el.index]
            const value = typeof raw === 'number' ? raw : Number(raw) || 0
            onSliceClick(label, value)
          }
        : undefined,
      plugins: {
        legend: { display: showLegend, position: legendPosition, labels: { padding: 16, font: { size: 12 } } },
        tooltip: {
          callbacks: formatTooltip
            ? {
                label: (ctx) => {
                  const raw = typeof ctx.raw === 'number' ? ctx.raw : 0
                  return formatTooltip(String(ctx.label ?? ''), raw)
                },
              }
            : undefined,
        },
        datalabels: { display: false },
      },
      animation: { duration: 600 },
    }),
    [showLegend, legendPosition, formatTooltip, onSliceClick],
  )

  return (
    <div
      className={className}
      style={{ height, width: '100%' }}
      role="img"
      aria-label={ariaLabel}
    >
      <Pie data={data} options={options} />
    </div>
  )
}
